import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Download, Eye, EyeOff, FileText, Info, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ExtractionStatusBadge } from '@/components/ui/status-badge';
import { DocumentPreviewDialog } from '@/components/documents/DocumentPreviewDialog';
import { DocumentViewer } from '@/components/documents/DocumentViewer';
import { DocumentPageContext } from '@/components/extraction/DocumentPageContext';
import { Evidence } from '@/components/extraction/Evidence';
import { CostRecordCard, InvoiceContractCard, InvoiceLinesCard } from '@/components/extraction/InvoiceCards';
import { useAuth } from '@/contexts/AuthContext';
import { useContract } from '@/services/contractService';
import { downloadDocument } from '@/services/documentService';
import { previewKind } from '@/utils/documentPreview';
import { useMediaQuery } from '@/lib/useMediaQuery';
import {
  useApplyExtraction,
  useDocumentExtraction,
  useExtractDocument,
  useExtraction,
  useRejectExtraction,
} from '@/services/extractionService';
import { apiErrorBody, describeApiError } from '@/utils/apiError';
import { formatDate } from '@/utils/formatters';
import {
  APPENDED_FIELDS,
  CLAUSE_TOPICS,
  LICENSE_TYPES,
  acceptPreselected,
  buildApplyRequest,
  createsCostRecord,
  displayValue,
  emptySelection,
  endDateAccepted,
  fieldLabel,
  fieldNeedsEdit,
  invoiceBlocker,
  invoiceTarget,
  isDecidableField,
  itemKey,
  itemRows,
  licenseBlockers,
  preselectCount,
  productNeedsEdit,
  termDatesAccepted,
  selectedCount,
  type ReviewSelection,
} from '@/utils/extractionReview';
import type { ExtractionDetail, ExtractionField, ExtractionFieldError, ExtractionItem } from '@/types';

const CONFLICT_MESSAGES: Record<string, string> = {
  contract_changed: 'The contract changed since this review was loaded.',
  licenses_changed: "The contract's licenses changed since this review was loaded.",
  not_in_review: 'This extraction is no longer waiting for review.',
  concurrent_apply: 'Someone else applied or rejected this extraction at the same time.',
  duplicate_appeared: 'A cost record with this invoice number was added since this review was loaded.',
};

const percent = (confidence: number) => `${Math.round(confidence * 100)}%`;

const editHint = (name: string): string => {
  if (name.endsWith('Date')) return 'yyyy-mm-dd';
  if (name === 'totalValue' || name === 'annualValue' || name === 'amount') return 'e.g. 43200.00';
  if (name === 'periodStart' || name === 'periodEnd') return 'yyyy-mm-dd';
  if (name === 'autoRenews') return 'true or false';
  return '';
};

/**
 * Review page for one document's AI extraction (#24, spec 6.3, 6.7): every proposal next to the live
 * contract value, with its quote and whether that quote was found in the document. Readable by every
 * role; accept, edit, apply and reject appear only for those who may manage contracts. Nothing is
 * selected until the reviewer clicks a row or the "Accept all above 0.8" shortcut.
 */
const ExtractionReview: React.FC = () => {
  const { id: contractId, documentId } = useParams({ strict: false }) as { id: string; documentId: string };
  const { canManageContracts, canManageCosts, canManageDocuments, canManageLicenses, canManageVendors } = useAuth();
  const navigate = useNavigate();
  const { data: contract } = useContract(contractId);
  const latest = useDocumentExtraction(documentId);
  const extractionId = latest.data?.id;
  const { data: detail, isLoading, error, refetch, isFetching } = useExtraction(extractionId);
  const applyMutation = useApplyExtraction();
  const rejectMutation = useRejectExtraction();
  const extractMutation = useExtractDocument();

  const [chosen, setSelection] = useState<ReviewSelection | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ExtractionFieldError[]>([]);
  const [conflict, setConflict] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ open: boolean; page?: number; jump: number }>({ open: false, jump: 0 });
  const previewToggle = useRef<HTMLButtonElement>(null);
  const sideBySide = useMediaQuery('(min-width: 1280px)');
  const showPage = useCallback((page: number) => setPreview((p) => ({ open: true, page, jump: p.jump + 1 })), []);
  const togglePreview = () => setPreview((p) => ({ open: !p.open, jump: p.jump + 1 }));
  const closePreview = () => setPreview((p) => ({ ...p, open: false }));

  // The selection belongs to the payload it was built from: a different extraction or contract version
  // starts again from nothing selected, so no choice is carried onto refreshed data (plan D13a)
  const initial = useMemo(() => (detail ? emptySelection(detail) : null), [detail]);
  const selection =
    chosen &&
    detail &&
    chosen.extractionId === detail.id &&
    chosen.contractVersion === detail.contractVersion &&
    chosen.licenseVersion === detail.licenseVersion
      ? chosen
      : initial;

  const isInvoice = detail?.schemaType === 'invoice';
  const editable = !!detail && canManageContracts && detail.status === 'Review' && (!isInvoice || canManageCosts);

  // A document the model reads as the other kind can be read again as that kind (spec 3.4)
  const rerunAs =
    detail?.status === 'Review' && canManageDocuments
      ? isInvoice && detail.documentKind !== 'invoice'
        ? ('contract' as const)
        : !isInvoice && detail.documentKind === 'invoice'
          ? ('invoice' as const)
          : null
      : null;

  const reload = async () => {
    setConflict(null);
    setFailure(null);
    setFieldErrors([]);
    setSelection(null);
    await latest.refetch();
    await refetch();
  };

  const handleError = (err: unknown) => {
    const body = apiErrorBody(err);
    if (body.status === 409) {
      setConflict(CONFLICT_MESSAGES[body.reason ?? ''] ?? describeApiError(err));
      return;
    }
    setFieldErrors((body.fieldErrors as ExtractionFieldError[] | undefined) ?? []);
    setFailure(describeApiError(err));
  };

  const apply = async () => {
    if (!detail || !selection) return;
    setFailure(null);
    setConflict(null);
    setFieldErrors([]);
    try {
      const result = await applyMutation.mutateAsync({ id: detail.id, request: buildApplyRequest(detail, selection) });
      // A moved document's review now lives under its new contract
      if (result.extraction.contractId !== contractId) {
        await navigate({
          to: '/contracts/$id/documents/$documentId/review',
          params: { id: result.extraction.contractId, documentId },
          replace: true,
        });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const rerun = async () => {
    if (!rerunAs) return;
    setFailure(null);
    try {
      await extractMutation.mutateAsync({ documentId, schema: rerunAs });
      await reload();
    } catch (err) {
      setFailure(describeApiError(err));
    }
  };

  const reject = async () => {
    if (!detail) return;
    setFailure(null);
    setConflict(null);
    try {
      await rejectMutation.mutateAsync(detail.id);
    } catch (err) {
      handleError(err);
    }
  };

  const back = (
    <Link
      to="/contracts/$id"
      params={{ id: contractId }}
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to {contract ? contract.contractNumber : 'the contract'}
    </Link>
  );

  if (latest.isLoading || isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
        {back}
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (latest.error || error) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
        {back}
        <Alert variant="destructive">
          <AlertDescription>Failed to load the review: {describeApiError(latest.error ?? error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!latest.data || !detail || detail.contractId !== contractId) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
        {back}
        <Alert>
          <AlertDescription>This document has no extraction to review.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // An invoice can only become a cost record with these two, so a missing one is a row to fill in
  const enterable = (f: ExtractionField) => isInvoice && (f.name === 'amount' || f.name === 'invoiceDate');
  const decidable = detail.fields.filter((f) => isDecidableField(f) && (f.found || (enterable(f) && detail.status === 'Review')));
  const notFound = detail.fields.filter((f) => isDecidableField(f) && !f.found && !enterable(f));
  const licenses = detail.items.filter((i) => i.group === 'license');
  const clauses = detail.items.filter((i) => i.group === 'clause');
  const proposedCurrency = detail.extractedCurrency ?? detail.contractCurrency;
  const errorsFor = (pred: (e: ExtractionFieldError) => boolean) => fieldErrors.filter(pred);
  const generalErrors = fieldErrors.filter((e) => !e.fieldId && e.itemIndex === undefined);

  const canPreview = previewKind(detail.documentFileName) !== null;
  const previewOpen = canPreview && preview.open;

  const review = (
    <div className="min-w-0 space-y-6 pb-8" data-testid="extraction-review">
      {back}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="flex flex-wrap items-center gap-2">
                Review extracted values
                <ExtractionStatusBadge status={detail.status} error={detail.errorMessage} />
              </CardTitle>
              <CardDescription className="mt-1 flex items-center gap-1.5 break-all">
                <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
                {detail.documentFileName}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              {canPreview && (
                <Button
                  ref={previewToggle}
                  variant="outline"
                  size="sm"
                  aria-expanded={previewOpen}
                  onClick={togglePreview}
                  data-testid="review-preview-toggle"
                >
                  {previewOpen ? (
                    <EyeOff className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  )}
                  {previewOpen ? 'Hide preview' : 'Preview'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadDocument(documentId, detail.documentFileName).catch(() => undefined)}
              >
                <Download className="h-4 w-4 mr-1.5" aria-hidden="true" />
                Download
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-xs text-grey-500">Contract</dt>
              <dd className="text-grey-800 break-words">
                {contract ? `${contract.contractNumber} · ${contract.title}` : detail.contractId}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-grey-500">Read by</dt>
              <dd className="text-grey-800">
                {detail.servedModel ?? detail.model ?? 'unknown model'}
                {detail.completedAt ? `, ${formatDate(detail.completedAt)}` : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-grey-500">Document kind</dt>
              <dd className="text-grey-800">{detail.documentKind ?? 'unknown'}</dd>
            </div>
          </dl>
          {detail.documentKind === 'amendment' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>Amendment: values replace the terms they amend.</AlertDescription>
            </Alert>
          )}
          {detail.imagesSent === false && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>Read from text only (over 50 pages): page images were not sent to the model.</AlertDescription>
            </Alert>
          )}
          {detail.warnings.map((warning) => (
            <Alert key={warning} variant="warning">
              <AlertDescription>{warning}</AlertDescription>
            </Alert>
          ))}
          {(detail.status === 'Pending' || detail.status === 'Processing') && (
            <Alert data-testid="review-in-flight">
              <AlertDescription className="flex flex-wrap items-center gap-3">
                The document is still being read. Refresh once it finishes to review the values.
                <Button size="sm" variant="outline" onClick={reload} disabled={isFetching}>
                  <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  Refresh
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {detail.status === 'Applied' && (
            <Alert variant="success">
              <AlertDescription>
                Applied by {detail.reviewedBy}
                {detail.reviewedAt ? ` on ${formatDate(detail.reviewedAt)}` : ''}. This review is read-only.
              </AlertDescription>
            </Alert>
          )}
          {detail.status === 'Rejected' && (
            <Alert>
              <AlertDescription>
                Rejected by {detail.reviewedBy}
                {detail.reviewedAt ? ` on ${formatDate(detail.reviewedAt)}` : ''}.{' '}
                {isInvoice ? 'No cost record was created.' : 'Nothing was written to the contract.'}
              </AlertDescription>
            </Alert>
          )}
          {detail.status === 'Review' && (!canManageContracts || (isInvoice && !canManageCosts)) && (
            <p className="text-sm text-grey-500">You can read these proposals; only Admins and Managers can apply them.</p>
          )}
          {rerunAs && (
            <Button size="sm" variant="outline" onClick={rerun} disabled={extractMutation.isPending} data-testid="review-rerun">
              <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />
              Extract as {rerunAs === 'invoice' ? 'an invoice' : 'a contract'}
            </Button>
          )}
        </CardContent>
      </Card>

      {conflict && (
        <Alert variant="warning" data-testid="review-conflict">
          <AlertTitle>{conflict}</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            Nothing was written. Reload to review the current values again.
            <Button size="sm" variant="outline" onClick={reload} disabled={isFetching}>
              <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />
              Reload review
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {failure && (
        <Alert variant="destructive" data-testid="review-error">
          <AlertTitle>{failure}</AlertTitle>
          {generalErrors.length > 0 && (
            <AlertDescription>
              <ul className="list-disc pl-5">
                {generalErrors.map((e, i) => (
                  <li key={i}>{e.message}</li>
                ))}
              </ul>
            </AlertDescription>
          )}
        </Alert>
      )}

      {selection && (
        <>
          {isInvoice ? (
            <InvoiceContractCard detail={detail} selection={selection} setSelection={setSelection} editable={editable && canManageDocuments} />
          ) : (
            <>
              <VendorCard detail={detail} selection={selection} setSelection={setSelection} editable={editable} canCreate={canManageVendors} />
              <OwnerCard detail={detail} selection={selection} setSelection={setSelection} editable={editable} />
            </>
          )}

          {isInvoice && <InvoiceLinesCard detail={detail} />}

          <Card>
            <CardHeader>
              <CardTitle>{isInvoice ? 'Invoice values' : 'Contract values'}</CardTitle>
              <CardDescription>
                {isInvoice
                  ? 'Accept the amount and the invoice date to create a cost record; the rest fills it in.'
                  : 'Current is the contract as it is now; proposed is what the document says.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {decidable.length === 0 && (
                <p className="text-sm text-grey-500">The document gives no {isInvoice ? 'invoice' : 'contract'} values.</p>
              )}
              {decidable.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  detail={detail}
                  proposedCurrency={proposedCurrency}
                  selection={selection}
                  setSelection={setSelection}
                  editable={editable}
                  errors={errorsFor((e) => e.fieldId === field.id)}
                />
              ))}
              {notFound.length > 0 && (
                <details className="text-sm text-grey-500">
                  <summary className="cursor-pointer">Not in the document ({notFound.length})</summary>
                  <ul className="mt-2 list-disc pl-5">
                    {notFound.map((f) => (
                      <li key={f.id}>{fieldLabel(f.name)}</li>
                    ))}
                  </ul>
                </details>
              )}
              {editable && detail.contractStatus === 'Draft' && endDateAccepted(detail, selection) && (
                <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm" data-testid="status-change">
                  <Checkbox
                    checked={selection.statusChange}
                    onCheckedChange={(c) => setSelection({ ...selection, statusChange: c === true })}
                    aria-label="Change status from Draft to Active"
                  />
                  <span>
                    Status: Draft to Active
                    <span className="block text-xs text-grey-500">Offered because the end date is accepted; never selected for you.</span>
                  </span>
                </label>
              )}
            </CardContent>
          </Card>

          {isInvoice && <CostRecordCard detail={detail} selection={selection} />}

          {licenses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Proposed licenses</CardTitle>
                <CardDescription>Each accepted item becomes a new license on this contract.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {licenses.map((item) => (
                  <LicenseItemRow
                    key={itemKey(item)}
                    item={item}
                    detail={detail}
                    proposedCurrency={proposedCurrency}
                    selection={selection}
                    setSelection={setSelection}
                    editable={editable && canManageLicenses}
                    errors={errorsFor((e) => e.group === 'license' && e.itemIndex === item.itemIndex)}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {clauses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Key clauses</CardTitle>
                <CardDescription>Accepted clauses are added to the contract notes, one line each.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {clauses.map((item) => (
                  <ClauseItemRow
                    key={itemKey(item)}
                    item={item}
                    detail={detail}
                    selection={selection}
                    setSelection={setSelection}
                    editable={editable}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {editable && (
            <ReviewFooter
              detail={detail}
              selection={selection}
              setSelection={setSelection}
              onApply={apply}
              onReject={reject}
              busy={applyMutation.isPending || rejectMutation.isPending}
              proposedCurrency={proposedCurrency}
              editable
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <DocumentPageContext.Provider value={canPreview ? showPage : null}>
      {previewOpen && sideBySide ? (
        <div className="grid grid-cols-2 items-start gap-6 px-4 py-8">
          {review}
          <aside
            className="sticky top-20 flex h-[calc(100vh-6rem)] flex-col gap-2 rounded-lg border border-border bg-white p-3"
            aria-label="Document preview"
            data-testid="review-preview-panel"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 truncate text-sm font-medium text-grey-800">{detail.documentFileName}</p>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close preview"
                onClick={() => {
                  // This button is about to go, and focus would fall back to the top of the page
                  previewToggle.current?.focus();
                  closePreview();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <DocumentViewer
              documentId={documentId}
              fileName={detail.documentFileName}
              page={preview.page}
              jump={preview.jump}
              className="flex-1"
            />
          </aside>
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-4 py-8">{review}</div>
      )}
      {previewOpen && !sideBySide && (
        <DocumentPreviewDialog
          documentId={documentId}
          fileName={detail.documentFileName}
          page={preview.page}
          jump={preview.jump}
          onClose={closePreview}
        />
      )}
    </DocumentPageContext.Provider>
  );
};

interface SectionProps {
  detail: ExtractionDetail;
  selection: ReviewSelection;
  setSelection: (s: ReviewSelection) => void;
  editable: boolean;
}

const DecisionBadge: React.FC<{ decision: string }> = ({ decision }) =>
  decision === 'Accepted' ? (
    <Badge variant="success">Accepted</Badge>
  ) : decision === 'Rejected' ? (
    <Badge variant="outline">Rejected</Badge>
  ) : null;

const FieldRow: React.FC<
  SectionProps & { field: ExtractionField; proposedCurrency?: string; errors: ExtractionFieldError[] }
> = ({ field, detail, selection, setSelection, editable, proposedCurrency, errors }) => {
  const choice = selection.fields[field.id] ?? { accepted: false };
  const needsEdit = fieldNeedsEdit(field);
  const [editing, setEditing] = useState(needsEdit);
  const appended = APPENDED_FIELDS.has(field.name);
  const canTick = !needsEdit || !!choice.edited?.trim();
  const set = (next: Partial<typeof choice>) =>
    setSelection({ ...selection, fields: { ...selection.fields, [field.id]: { ...choice, ...next } } });
  const label = fieldLabel(field.name);
  const open = detail.status === 'Review';
  const invoice = field.group === 'invoice'; // a new cost record replaces nothing: no current value to compare

  return (
    <div
      className={`grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-md border border-border p-3 ${
        invoice ? 'md:grid-cols-[auto_minmax(0,1fr)_minmax(0,2fr)]' : 'md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)]'
      }`}
      data-testid={`field-${field.name}`}
    >
      <div className="pt-0.5">
        {editable ? (
          <Checkbox
            checked={choice.accepted}
            disabled={!canTick}
            onCheckedChange={(c) => set({ accepted: c === true })}
            aria-label={`Accept ${label}`}
          />
        ) : (
          <span className="block w-4" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-grey-800">{label}</p>
        {/* Comparisons with the live contract mean something only while the review is open: once applied,
            the contract holds the accepted values and every one of them would read "Matches" */}
        <div className="mt-1 flex flex-wrap gap-1">
          {open && !invoice && field.isGap && !appended && <Badge variant="info">Fills a gap</Badge>}
          {open && !invoice && field.matchesLive && <Badge variant="success">Matches</Badge>}
          {open && !invoice && !field.isGap && !field.matchesLive && !appended && <Badge variant="warning">Differs</Badge>}
          {!field.stated && <Badge variant="outline">Computed</Badge>}
          {open && field.currencyBlocked && <Badge variant="warning">Other currency</Badge>}
          <DecisionBadge decision={field.decision} />
        </div>
        {open && !invoice && field.changedSinceExtraction && (
          <p className="mt-1 text-xs text-warning-dark">
            Changed since extraction (was {displayValue(field.name, field.currentValue, detail.contractCurrency) || 'blank'})
          </p>
        )}
      </div>
      {!invoice && (
        <div className="col-start-2 md:col-start-auto min-w-0 text-sm">
          <p className="text-xs text-grey-500">{open ? 'Current' : 'Contract now'}</p>
          <p className="text-grey-700 break-words">
            {appended ? 'Added to notes' : displayValue(field.name, field.liveValue, detail.contractCurrency) || '—'}
          </p>
          {field.decision !== 'Pending' && field.appliedAt && (
            <p className="mt-1 text-xs text-grey-500 break-words">
              Applied: {displayValue(field.name, field.oldValue, detail.contractCurrency) || 'blank'} to{' '}
              {displayValue(field.name, field.appliedValue, proposedCurrency) || field.appliedValue}
            </p>
          )}
        </div>
      )}
      <div className="col-start-2 md:col-start-auto min-w-0 text-sm space-y-1.5">
        <p className="text-xs text-grey-500">Proposed · {percent(field.confidence)} confidence</p>
        {!field.found ? (
          <p className="text-grey-500 text-xs">Not in the document: enter it to use it.</p>
        ) : field.isValid ? (
          <p className="text-grey-900 font-medium break-words">{displayValue(field.name, field.value, proposedCurrency)}</p>
        ) : (
          <p className="text-destructive text-xs break-words">
            {field.rawValue ? `"${field.rawValue}": ` : ''}
            {field.validationMessage ?? 'Not a valid value'}
          </p>
        )}
        {editable && editing && (
          <div>
            <Label htmlFor={`edit-${field.id}`} className="sr-only">
              Corrected {label}
            </Label>
            <Input
              id={`edit-${field.id}`}
              value={choice.edited ?? ''}
              placeholder={editHint(field.name) || 'Corrected value'}
              onChange={(e) => {
                const value = e.target.value;
                set({ edited: value, accepted: needsEdit ? value.trim() !== '' && choice.accepted : choice.accepted });
              }}
              className="h-8 text-sm"
            />
          </div>
        )}
        {editable && !needsEdit && (
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => {
              if (editing) set({ edited: undefined });
              setEditing(!editing);
            }}
          >
            {editing ? 'Use the proposed value' : 'Edit value'}
          </button>
        )}
        <Evidence quote={field.citedText} location={field.location} verified={field.quoteVerified} />
        {errors.map((e, i) => (
          <p key={i} className="text-xs text-destructive" role="alert">
            {e.message}
          </p>
        ))}
      </div>
    </div>
  );
};

const VendorCard: React.FC<SectionProps & { canCreate: boolean }> = ({ detail, selection, setSelection, editable, canCreate }) => {
  const { vendor } = detail;
  const row = detail.fields.find((f) => f.name === 'vendorName' && f.itemIndex === undefined);
  if (!row || !row.found) return null;
  const candidate = vendor.candidates[0];
  const setVendor = (next: ReviewSelection['vendor']) => setSelection({ ...selection, vendor: next });
  const action = selection.vendor.action;

  let summary: string;
  switch (vendor.matchKind) {
    case 'exact':
      summary = vendor.matchesCurrent ? "Matches the contract's vendor" : `Matches ${candidate?.name}, not the contract's vendor`;
      break;
    case 'probable':
      summary = `Probably ${candidate?.name} (equal once the legal suffix is removed)`;
      break;
    case 'ambiguous':
      summary = `Several vendors match: pick one, or keep the current vendor`;
      break;
    default:
      summary = `No vendor named ${vendor.proposedName}`;
  }

  return (
    <Card data-testid="vendor-card">
      <CardHeader>
        <CardTitle>Vendor</CardTitle>
        <CardDescription>A different vendor moves the contract between vendor reports, so it always needs a choice.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-grey-500">Current</p>
            <p className="text-grey-800">{vendor.currentVendorName ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-grey-500">The document names · {percent(row.confidence)} confidence</p>
            <p className="text-grey-900 font-medium break-words">{vendor.proposedName}</p>
            <p className="text-xs text-grey-600">{summary}</p>
          </div>
        </div>
        <Evidence quote={row.citedText} location={row.location} verified={row.quoteVerified} />
        {editable && !vendor.matchesCurrent && (
          <fieldset className="space-y-2" aria-label="Vendor choice">
            <label className="flex items-center gap-2">
              <input type="radio" name="vendor" checked={action === 'keep'} onChange={() => setVendor({ action: 'keep' })} />
              Keep {vendor.currentVendorName ?? 'the current vendor'}
            </label>
            {(vendor.matchKind === 'exact' || vendor.matchKind === 'probable') && candidate && (
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vendor"
                  checked={action === 'select' && selection.vendor.vendorId === candidate.id}
                  onChange={() => setVendor({ action: 'select', vendorId: candidate.id })}
                />
                Change to {candidate.name}
              </label>
            )}
            {vendor.matchKind === 'ambiguous' && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="radio"
                  name="vendor"
                  aria-label="Change to the picked vendor"
                  checked={action === 'select'}
                  onChange={() => setVendor({ action: 'select', vendorId: selection.vendor.vendorId ?? vendor.candidates[0]?.id })}
                />
                <Select
                  value={action === 'select' ? selection.vendor.vendorId : undefined}
                  onValueChange={(id) => setVendor({ action: 'select', vendorId: id })}
                >
                  <SelectTrigger className="h-8 w-64 max-w-full">
                    <SelectValue placeholder="Pick a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendor.candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {vendor.matchKind !== 'exact' && canCreate && (
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vendor"
                  checked={action === 'create'}
                  onChange={() => setVendor({ action: 'create', name: vendor.proposedName })}
                />
                Create vendor {vendor.proposedName}
              </label>
            )}
          </fieldset>
        )}
      </CardContent>
    </Card>
  );
};

const OwnerCard: React.FC<SectionProps> = ({ detail, selection, setSelection, editable }) => {
  const contacts = detail.fields.filter(
    (f) => f.itemIndex === undefined && f.found && (f.name === 'customerContactName' || f.name === 'customerContactEmail'),
  );
  if (contacts.length === 0) return null;
  const { owner } = detail;
  const selected = selection.owner.action === 'select';

  return (
    <Card data-testid="owner-card">
      <CardHeader>
        <CardTitle>Customer contact</CardTitle>
        <CardDescription>The contact named in the document, a hint for the contract owner.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {contacts.map((f) => (
          <div key={f.id}>
            <p className="text-xs text-grey-500">
              {fieldLabel(f.name)} · {percent(f.confidence)} confidence
            </p>
            <p className="text-grey-900 break-words">{f.value}</p>
            <Evidence quote={f.citedText} location={f.location} verified={f.quoteVerified} className="mt-1" />
          </div>
        ))}
        {owner.matchedUserId ? (
          owner.matchesCurrent ? (
            <p className="text-grey-600">{owner.matchedName} is already the owner.</p>
          ) : editable ? (
            <label className="flex items-start gap-2">
              <Checkbox
                checked={selected}
                onCheckedChange={(c) =>
                  setSelection({ ...selection, owner: c === true ? { action: 'select', userId: owner.matchedUserId } : { action: 'keep' } })
                }
                aria-label={`Set owner to ${owner.matchedName}`}
              />
              <span>
                Set owner to {owner.matchedName}
                <span className="block text-xs text-grey-500">Current owner: {owner.currentOwnerName ?? 'none'}</span>
              </span>
            </label>
          ) : (
            <p className="text-grey-600">Matches {owner.matchedName}.</p>
          )
        ) : (
          <p className="text-grey-500">No active user matches this contact; shown for information only.</p>
        )}
      </CardContent>
    </Card>
  );
};

const LicenseItemRow: React.FC<
  SectionProps & { item: ExtractionItem; proposedCurrency?: string; errors: ExtractionFieldError[] }
> = ({ item, detail, selection, setSelection, editable, proposedCurrency, errors }) => {
  const rows = itemRows(detail, item);
  const value = (name: string) => rows.find((r) => r.name === name)?.value;
  // What the apply wrote (edits and the annual cost included): the audit trail once the review is done
  const applied = (name: string) => rows.find((r) => r.name === name)?.appliedValue;
  const choice = selection.items[itemKey(item)] ?? { accepted: false, edits: {} };
  const blockers = licenseBlockers(item, choice.edits, rows, termDatesAccepted(detail, selection));
  const needsProduct = productNeedsEdit(rows);
  const set = (next: Partial<typeof choice>) =>
    setSelection({ ...selection, items: { ...selection.items, [itemKey(item)]: { ...choice, ...next } } });
  const setEdit = (key: string, v: string) => set({ edits: { ...choice.edits, [key]: v } });
  const product = value('product') ?? 'Unnamed product';

  return (
    <div className="rounded-md border border-border p-3 space-y-2 text-sm" data-testid={`license-${item.itemIndex}`}>
      <div className="flex items-start gap-3">
        {editable ? (
          <Checkbox
            checked={choice.accepted}
            disabled={blockers.length > 0}
            onCheckedChange={(c) => set({ accepted: c === true })}
            aria-label={`Accept license ${product}`}
          />
        ) : (
          <span className="block w-4" />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium text-grey-900 break-words">{product}</p>
          <p className="text-grey-700">
            {value('quantity') ?? '?'} × {displayValue('unitCost', value('unitCost'), proposedCurrency) || 'no price'} per{' '}
            {displayValue('unitCostPeriod', value('unitCostPeriod')) || 'unknown period'} · {value('licenseType') ?? 'unknown type'} ·{' '}
            {percent(item.confidence)} confidence
          </p>
          {item.annualUnitCost !== undefined && item.annualUnitCost !== null && (
            <p className="text-xs text-grey-500">
              Annual cost per unit: {displayValue('unitCost', String(item.annualUnitCost), proposedCurrency)}
            </p>
          )}
          {item.decision === 'Accepted' && (
            <p className="text-xs text-success-dark break-words" data-testid={`license-${item.itemIndex}-applied`}>
              Created as {applied('product') ?? product}: {applied('quantity') ?? value('quantity')} ×{' '}
              {displayValue('unitCost', applied('unitCost'), proposedCurrency)} a year per unit, {applied('licenseType') ?? value('licenseType')}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            {item.decision === 'Pending' && item.duplicateProduct && <Badge variant="warning">Already on the contract</Badge>}
            {item.decision === 'Pending' && item.currencyBlocked && <Badge variant="warning">Other currency</Badge>}
            <DecisionBadge decision={item.decision} />
          </div>
          {editable && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <div>
                <Label className="text-xs text-grey-500" htmlFor={`lic-qty-${item.itemIndex}`}>
                  Quantity
                </Label>
                <Input
                  id={`lic-qty-${item.itemIndex}`}
                  className="h-8"
                  value={choice.edits.quantity ?? ''}
                  placeholder={value('quantity') ?? ''}
                  onChange={(e) => setEdit('quantity', e.target.value)}
                />
              </div>
              {(item.needsLicenseType || choice.edits.licenseType) && (
                <div>
                  <Label className="text-xs text-grey-500">License type</Label>
                  <Select value={choice.edits.licenseType} onValueChange={(v) => setEdit('licenseType', v)}>
                    <SelectTrigger className="h-8" aria-label="License type">
                      <SelectValue placeholder="Pick a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {item.needsAnnualUnitCost && (
                <div>
                  <Label className="text-xs text-grey-500" htmlFor={`lic-annual-${item.itemIndex}`}>
                    Annual cost per unit
                  </Label>
                  <Input
                    id={`lic-annual-${item.itemIndex}`}
                    className="h-8"
                    value={choice.edits.annualUnitCost ?? ''}
                    placeholder="e.g. 360.00"
                    onChange={(e) => setEdit('annualUnitCost', e.target.value)}
                  />
                </div>
              )}
              {(item.duplicateProduct || needsProduct) && (
                <div>
                  <Label className="text-xs text-grey-500" htmlFor={`lic-product-${item.itemIndex}`}>
                    {item.duplicateProduct ? 'Different product name' : 'Product name'}
                  </Label>
                  <Input
                    id={`lic-product-${item.itemIndex}`}
                    className="h-8"
                    value={choice.edits.product ?? ''}
                    onChange={(e) => setEdit('product', e.target.value)}
                  />
                </div>
              )}
            </div>
          )}
          {editable && blockers.length > 0 && <p className="text-xs text-grey-500">{blockers.join('; ')} to accept it.</p>}
          <Evidence quote={item.citedText} location={item.location} verified={item.quoteVerified} />
          {errors.map((e, i) => (
            <p key={i} className="text-xs text-destructive" role="alert">
              {e.property ? `${fieldLabel(e.property)}: ` : ''}
              {e.message}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

const ClauseItemRow: React.FC<SectionProps & { item: ExtractionItem }> = ({ item, detail, selection, setSelection, editable }) => {
  const rows = itemRows(detail, item);
  const topic = rows.find((r) => r.name === 'topic')?.value;
  const summary = rows.find((r) => r.name === 'summary')?.value;
  const choice = selection.items[itemKey(item)] ?? { accepted: false, edits: {} };
  const valid = rows.every((r) => r.found && r.isValid);

  return (
    <div className="flex items-start gap-3 rounded-md border border-border p-3 text-sm" data-testid={`clause-${item.itemIndex}`}>
      {editable ? (
        <Checkbox
          checked={choice.accepted}
          disabled={!valid}
          onCheckedChange={(c) =>
            setSelection({ ...selection, items: { ...selection.items, [itemKey(item)]: { ...choice, accepted: c === true } } })
          }
          aria-label={`Accept clause ${topic ? CLAUSE_TOPICS[topic] ?? topic : ''}`}
        />
      ) : (
        <span className="block w-4" />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium text-grey-900">
          {topic ? CLAUSE_TOPICS[topic] ?? topic : 'Clause'} <DecisionBadge decision={item.decision} />
        </p>
        <p className="text-grey-700 break-words">{summary}</p>
        <Evidence quote={item.citedText} location={item.location} verified={item.quoteVerified} />
      </div>
    </div>
  );
};

const ReviewFooter: React.FC<
  SectionProps & { onApply: () => void; onReject: () => void; busy: boolean; proposedCurrency?: string }
> = ({ detail, selection, setSelection, onApply, onReject, busy, proposedCurrency }) => {
  const count = selectedCount(detail, selection);
  const shortcut = preselectCount(detail);
  const isInvoice = detail.schemaType === 'invoice';
  const blocker = invoiceBlocker(detail, selection);
  const moveTarget = selection.moveTo ? invoiceTarget(detail, selection) : undefined;
  const selectedRows = useMemo(
    () => detail.fields.filter((f) => isDecidableField(f) && selection.fields[f.id]?.accepted),
    [detail, selection],
  );
  const selectedItems = detail.items.filter((i) => selection.items[itemKey(i)]?.accepted);

  return (
    <Card className="border-primary/40" data-testid="review-footer">
      <CardHeader>
        <CardTitle>Apply</CardTitle>
        <CardDescription>
          {shortcut > 0
            ? isInvoice
              ? `${shortcut} value${shortcut === 1 ? '' : 's'} are stated in the document, verified in its text and at least 80% confident. The contract is your choice.`
              : `${shortcut} value${shortcut === 1 ? '' : 's'} are stated in the document, verified in its text, at least 80% confident, and fill a gap or match.`
            : 'No value qualifies for the shortcut: select what to apply by hand.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setSelection(acceptPreselected(detail, selection))} disabled={shortcut === 0}>
            Accept all above 0.8
          </Button>
          <Button variant="ghost" onClick={() => setSelection(emptySelection(detail))}>
            Clear
          </Button>
        </div>

        {count > 0 && (
          <div>
            <p className="text-sm font-medium text-grey-800 mb-2">Selected ({count})</p>
            <ul className="space-y-2 text-sm" data-testid="selected-list">
              {selectedRows.map((f) => (
                <li key={f.id} className="rounded-md bg-grey-50 p-2">
                  <span className="font-medium">{fieldLabel(f.name)}:</span>{' '}
                  {selection.fields[f.id]?.edited?.trim() || displayValue(f.name, f.value, proposedCurrency)}
                  <Evidence quote={f.citedText} location={f.location} verified={f.quoteVerified} className="mt-1" />
                </li>
              ))}
              {selectedItems.map((i) => {
                const rows = itemRows(detail, i);
                const name = rows.find((r) => r.name === (i.group === 'license' ? 'product' : 'summary'))?.value;
                return (
                  <li key={itemKey(i)} className="rounded-md bg-grey-50 p-2">
                    <span className="font-medium">{i.group === 'license' ? 'New license' : 'Clause'}:</span> {name}
                    <Evidence quote={i.citedText} location={i.location} verified={i.quoteVerified} className="mt-1" />
                  </li>
                );
              })}
              {moveTarget && (
                <li className="rounded-md bg-grey-50 p-2 break-words">
                  Move the document to {moveTarget.contractNumber} · {moveTarget.title}
                </li>
              )}
              {selection.vendor.action !== 'keep' && <li className="rounded-md bg-grey-50 p-2">Vendor change</li>}
              {selection.owner.action === 'select' && <li className="rounded-md bg-grey-50 p-2">Owner: {detail.owner.matchedName}</li>}
              {selection.statusChange && detail.contractStatus === 'Draft' && endDateAccepted(detail, selection) && (
                <li className="rounded-md bg-grey-50 p-2">Status: Draft to Active</li>
              )}
            </ul>
          </div>
        )}

        {blocker && (
          <p className="text-sm text-warning-dark" data-testid="review-blocker">
            {blocker}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={busy}>
                Reject all
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject this extraction?</AlertDialogTitle>
                <AlertDialogDescription>
                  {isInvoice ? 'No cost record is created and the document stays where it is.' : 'Nothing is written to the contract.'} The review
                  becomes read-only.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onReject}>Reject all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={busy || count === 0 || !!blocker}>
                Apply {count} value{count === 1 ? '' : 's'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Apply {count} value{count === 1 ? '' : 's'}
                  {isInvoice ? '?' : ' to the contract?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isInvoice
                    ? `${createsCostRecord(detail, selection) ? `A cost record is created on ${(moveTarget ?? invoiceTarget(detail, selection))?.contractNumber ?? 'the contract'}. ` : 'No cost record is created. '}${moveTarget ? `The document moves to ${moveTarget.contractNumber}. ` : ''}Everything else is recorded as rejected. An applied review cannot be applied again.`
                    : 'The selected values are written to the contract as your edit; everything else is recorded as rejected. An applied review cannot be applied again.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onApply}>Apply {count}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExtractionReview;
